import React, { useState } from 'react';
import { Button, Form, InputGroup, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import EventModalMediaContent from '../../../../../components/EventModalMediaContent';
import SystemAlert from '../../../../../components/modal/SystemAlert';
import { useOrderContext } from '../../../../../providers/OrderProvider';
import { useOrderDetailViewOnly } from './OrderDetailViewOnlyContext';
import { useAddOrderContractsMutation, useRemoveOrderContractMutation } from '../../../../../slices/ordersApiSlice';
import { markLocalOrderMutation } from '../../../../../utils/order/orderLocalMutationGuard';

const pad2 = (n) => String(n).padStart(2, '0');

const formatDateTimeDisplay = (value) => {
	if (!value) return '-';
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return '-';
	return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const formatDateTimeCopy = (value) => {
	if (!value) return '-';
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return '-';
	return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
};

const OrderContractsDetail = ({ contractTooltipId = 'order-detail-contract-toggle-tooltip' }) => {
	const { order, isOrderReady } = useOrderContext();
	const viewOnly = useOrderDetailViewOnly();
	const isApprovalRejected = order?.approvalStatus === 'reject';
	const hideMutations = isApprovalRejected || viewOnly;
	const actionDisabled = !isOrderReady || isApprovalRejected;
	const [showAddInput, setShowAddInput] = useState(false);
	const [addValue, setAddValue] = useState('');
	const [addOrderContracts, { isLoading }] = useAddOrderContractsMutation();
	const [removeOrderContract, { isLoading: isDeleting }] = useRemoveOrderContractMutation();
	const [deletingContractNumber, setDeletingContractNumber] = useState('');
	const [confirmContractNumber, setConfirmContractNumber] = useState('');

	const contracts = Array.isArray(order?.contracts)
		? order.contracts
				.map((item) => {
					if (item && typeof item === 'object' && item.contractNumber != null && item.contractNumber !== '') {
						return {
							contractNumber: String(item.contractNumber).trim(),
							createdAt: item.createdAt,
						};
					}
					return null;
				})
				.filter(Boolean)
		: [];

	const handleAddContracts = async () => {
		const trimmed = addValue.trim();
		if (!order?._id || !trimmed) return;

		markLocalOrderMutation(order._id);
		await addOrderContracts({ _id: order._id, contractNumbers: [trimmed] }).unwrap();
		setAddValue('');
		setShowAddInput(false);
	};

	const handleCopyAddedTime = async (createdAt) => {
		const text = formatDateTimeCopy(createdAt);
		if (text === '-') return;
		try {
			await navigator.clipboard.writeText(text);
			toast.success('添加时间已复制');
		} catch {
			toast.error('复制失败');
		}
	};

	const handleRemoveContract = async () => {
		if (!order?._id || !confirmContractNumber) return;

		setDeletingContractNumber(confirmContractNumber);
		try {
			markLocalOrderMutation(order._id);
			await removeOrderContract({ _id: order._id, contractNumber: confirmContractNumber }).unwrap();
			setConfirmContractNumber('');
		} finally {
			setDeletingContractNumber('');
		}
	};

	return (
		<EventModalMediaContent
			icon='file-contract'
			heading='合同编号'
			headingAction={
				hideMutations ? null : (
				<OverlayTrigger placement='top' overlay={<Tooltip id={contractTooltipId}>{showAddInput ? '收起' : '补充合同编号'}</Tooltip>}>
					<Button size='sm' className='p-0 border-0 text-warning' variant='link' disabled={!isOrderReady} onClick={() => setShowAddInput((v) => !v)}>
						<FontAwesomeIcon icon={showAddInput ? 'xmark' : 'plus'} />
					</Button>
				</OverlayTrigger>
				)
			}
		>
			<div>
				{contracts.length === 0 && (
					<div className='text-muted mb-0'>
						<span className='small'>暂无合同编号</span>
					</div>
				)}
				{contracts.map((contract, index) => {
					const addedTime = formatDateTimeDisplay(contract.createdAt);
					const canCopyTime = addedTime !== '-';
					return (
						<div key={`${contract.contractNumber}-${index}`} className='mb-2 d-flex align-items-center gap-2'>
							<span className='text-body'># {contract.contractNumber}</span>
							<div className='ms-auto d-flex align-items-center gap-2 flex-shrink-0'>
								<OverlayTrigger placement='top' overlay={<Tooltip id={`contract-added-time-${contract.contractNumber}-${index}`}>点击复制添加时间</Tooltip>}>
									<span
										role={canCopyTime ? 'button' : undefined}
										tabIndex={canCopyTime ? 0 : undefined}
										className={`fs-11 text-600 text-nowrap mb-0${canCopyTime ? ' cursor-pointer' : ''}`}
										onClick={canCopyTime ? () => handleCopyAddedTime(contract.createdAt) : undefined}
										onKeyDown={
											canCopyTime
												? (e) => {
														if (e.key === 'Enter' || e.key === ' ') {
															e.preventDefault();
															handleCopyAddedTime(contract.createdAt);
														}
													}
												: undefined
										}
									>
										{addedTime}
									</span>
								</OverlayTrigger>
								{hideMutations ? null : (
								<Button
									size='sm'
									variant='link'
									className='p-0 fs-11 text-danger'
									onClick={() => setConfirmContractNumber(contract.contractNumber)}
									disabled={actionDisabled || (isDeleting && deletingContractNumber === contract.contractNumber)}
									title='删除'
								>
									{isDeleting && deletingContractNumber === contract.contractNumber ? <Spinner size='sm' animation='border' /> : <FontAwesomeIcon icon='trash' />}
								</Button>
								)}
							</div>
						</div>
					);
				})}
				<SystemAlert
					title='提示内容'
					bodyText={`你确定要删除合同编号「${confirmContractNumber}」吗？`}
					cancelText='手滑了'
					confirmText='确定删除'
					confirmColor='danger'
					confirmWaiting={isDeleting}
					modalShow={Boolean(confirmContractNumber)}
					setModalShow={() => !isDeleting && setConfirmContractNumber('')}
					handleConfirm={handleRemoveContract}
				/>
				{showAddInput && (
					<InputGroup className='mt-2'>
						<Form.Label htmlFor='orderDetailContractNumber' className='visually-hidden'>
							合同编号
						</Form.Label>
						<Form.Control
							id='orderDetailContractNumber'
							name='orderDetailContractNumber'
							size='sm'
							className='border-warning-subtle'
							placeholder='请输入合同编号'
							value={addValue}
							onChange={(e) => setAddValue(e.target.value)}
							disabled={actionDisabled || isLoading}
						/>
						<Button size='sm' className='fs-11' variant='outline-primary' onClick={handleAddContracts} disabled={actionDisabled || isLoading || !addValue.trim()}>
							{isLoading ? '添加中...' : '添加'}
						</Button>
					</InputGroup>
				)}
			</div>
		</EventModalMediaContent>
	);
};

export default OrderContractsDetail;
